import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Trash2 } from 'lucide-react';
import { Habit } from '../utils/storage';
import { isCompletedToday, wasMissedYesterday, getStreak } from '../utils/habits';
import { StreakBadge } from './StreakBadge';
interface HabitCardProps {
  habit: Habit;
  onComplete: (id: string) => void;
  onDelete?: (id: string) => void;
}
export const HabitCard: React.FC<HabitCardProps> = ({
  habit,
  onComplete,
  onDelete
}) => {
  const [showToast, setShowToast] = useState(false);
  const [deleteState, setDeleteState] = useState<'idle' | 'confirming'>('idle');
  const completed = isCompletedToday(habit);
  const missedYesterday = wasMissedYesterday(habit);
  const streak = getStreak(habit);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleComplete = () => {
    if (completed || deleteState === 'confirming') return;
    onComplete(habit.id);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 2500);
  };
  const handlePointerDown = () => {
    longPressTimer.current = setTimeout(() => {
      setDeleteState('confirming');
    }, 600);
  };
  const handlePointerUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(habit.id);
  };
  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteState('idle');
  };
  return <div className="relative w-full mb-4" data-id="element-20">
      <motion.div layout onClick={deleteState === 'idle' ? handleComplete : undefined} onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} className={`p-5 rounded-2xl border transition-colors duration-500 ${completed ? 'bg-[#F2F7F4] border-[#6B8F71]/30' : 'bg-white border-gray-100 shadow-sm cursor-pointer active:scale-[0.98]'}`} data-id="element-21">
        <div className="flex items-center justify-between" data-id="element-22">
          <div className="flex-1 pr-4" data-id="element-23">
            <div className="flex items-center space-x-2 mb-1" data-id="element-24">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-25">
                {habit.identity}
              </span>
              <StreakBadge streak={streak} data-id="element-26" />
            </div>
            <h3 className={`text-lg font-medium transition-colors duration-300 ${completed ? 'text-gray-700' : 'text-gray-900'}`} data-id="element-27">
              {habit.name}
            </h3>

            {habit.cue && !completed && <p className="text-sm text-gray-400 mt-1" data-id="element-28">When: {habit.cue}</p>}

            {missedYesterday && !completed && <motion.p initial={{
            opacity: 0,
            y: 5
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-sm text-amber-500 mt-2 font-medium" data-id="element-29">
                Get back on track today
              </motion.p>}
          </div>

          <AnimatePresence mode="wait" data-id="element-30">
            {deleteState === 'confirming' ? <motion.div key="delete-confirm" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} className="flex items-center space-x-2 flex-shrink-0" data-id="element-31">
                <button type="button" onClick={handleDelete} className="flex items-center space-x-1 px-3 py-2 rounded-full bg-red-50 text-red-400 text-xs font-medium hover:bg-red-100 transition-colors" data-id="element-32">
                  <Trash2 size={14} data-id="element-33" />
                  <span data-id="element-34">Delete</span>
                </button>
                <button type="button" onClick={handleCancelDelete} className="px-3 py-2 rounded-full bg-gray-100 text-gray-500 text-xs font-medium hover:bg-gray-200 transition-colors" data-id="element-35">
                  Cancel
                </button>
              </motion.div> : <motion.button key="complete-btn" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} type="button" onClick={e => {
            e.stopPropagation();
            handleComplete();
          }} disabled={completed} className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${completed ? 'bg-[#6B8F71] text-white cursor-default' : 'bg-gray-50 border-2 border-gray-200 text-transparent cursor-pointer hover:border-[#6B8F71]/50 hover:bg-[#6B8F71]/5 active:scale-95'}`} aria-label={completed ? 'Completed' : 'Mark complete'} data-id="element-36">
                <AnimatePresence data-id="element-37">
                  {completed && <motion.div initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20
              }} data-id="element-38">
                      <Check size={24} strokeWidth={3} data-id="element-39" />
                    </motion.div>}
                </AnimatePresence>
              </motion.button>}
          </AnimatePresence>
        </div>
      </motion.div>

      <AnimatePresence data-id="element-40">
        {showToast && <motion.div initial={{
        opacity: 0,
        y: -10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 8,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -10,
        scale: 0.95
      }} className="absolute left-0 right-0 flex justify-center z-20 pointer-events-none" data-id="element-41">
            <div className="bg-gray-900 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center space-x-2" data-id="element-42">
              <span className="text-[#6B8F71]" data-id="element-43">✨</span>
              <span data-id="element-44">
                You just voted for being a {habit.identity.toLowerCase()}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};